import { ColumnFilter } from "src/base-components/ColumnFilter";
import { StringFilter } from "src/base-components/tableColumnFilters/StringFilter";
import { useFilter } from "src/base-components/tableColumnFilters/useFilters";
import { useQueueMembers } from "src/cases/queues/hooks";
import { CaseQueueColumnSchema } from "src/cases/queues/types";
import { CasesPageParams } from "src/router/urls";
import { assertUnreachable } from "src/utils/typeUtils";
import { useParamsDecode } from "src/utils/useParamsDecode";
import { getUserName } from "src/utils/user";

const NO_ASSIGNEE_OPTION = {
  key: "null",
  value: "Unassigned",
} as const;

const DELETED_ASSIGNEES_OPTION = {
  key: "deleted_assignees",
  value: "Deleted Assignees",
} as const;

export const AssigneeFilter = () => {
  const { queue_id } = useParamsDecode<CasesPageParams>();
  const { data: members } = useQueueMembers(queue_id);

  const assigneeOptions = [
    NO_ASSIGNEE_OPTION,
    DELETED_ASSIGNEES_OPTION,
    ...(members?.map((user) => ({
      key: user.id,
      value: getUserName(user) ?? "Unknown user",
    })) ?? []),
  ];

  return (
    <EqColumnFilter
      dataLoc="cases-table-filter-assignee"
      displayName="Assignee"
      elements={assigneeOptions}
      property="assignee"
    />
  );
};

type EqColumnFilterElement = {
  key: string;
  value: string;
};

export const EqColumnFilter: React.FC<{
  property: string;
  displayName: string;
  dataLoc?: string;
  elements: EqColumnFilterElement[];
}> = ({ displayName, property, elements, dataLoc }) => {
  const { filters, update, apply, reset, isFiltering, isApplied } = useFilter(
    property,
    [
      {
        key: property,
        operator: "eq",
        values: [],
      },
    ],
  );

  const filter = filters[0];
  const selected = filter.values[0];

  return (
    <ColumnFilter
      dataLoc={dataLoc}
      description={`Filter by ${displayName}`}
      elements={elements}
      isApplied={isApplied()}
      isFiltering={isFiltering({ key: property })}
      placement="bottom start"
      selected={selected}
      onApply={apply}
      onResetRequest={() => reset()}
      onSelect={(value, event) => {
        event.stopPropagation();
        update([
          {
            ...filter,
            values: filter.values[0] === value ? [] : [value],
          },
        ]);
      }}
    />
  );
};

export const CasePropertyFilter: React.FC<{
  columnSchema: CaseQueueColumnSchema;
}> = ({ columnSchema }) => {
  if (columnSchema.type === "enum") {
    return (
      <EqColumnFilter
        dataLoc={`cases-table-filter-${columnSchema.__key}`}
        displayName={columnSchema.display_name}
        elements={columnSchema.options.map((value) => ({
          key: value,
          value,
        }))}
        property={`properties.${columnSchema.__key}`}
      />
    );
  }

  return renderFilterByType(columnSchema);
};

const renderFilterByType = (columnSchema: CaseQueueColumnSchema) => {
  switch (columnSchema?.type) {
    case "string": {
      return (
        <StringFilter
          dataLoc={`cases-table-filter-${columnSchema.__key}`}
          displayName={columnSchema.display_name}
          operators={["eq", "contains"]}
          property={`properties.${columnSchema.__key}`}
        />
      );
    }

    case "event_ref":
    case "entity_ref":
    case "datetime":
    case "tags": {
      return null;
    }

    default:
      assertUnreachable(columnSchema as never);
  }
};
